import { NavigationHelpers, ParamListBase } from '@react-navigation/native';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { NavDrawerEventMap } from '@components/Web/Navigator/WebDrawerNavigator';
import {
  GetTeamDetailDocument,
  Team,
  useCancelStripeSubscriptionMutation,
} from '@graphql/generated';
interface CancelSubscriptionModalProps {
  visible: boolean;
  onClose: () => void;
  selectedTeam?: Team; // Adjust the type as necessary
  afterSubmitCallback: () => void;
  navigation: NavigationHelpers<ParamListBase, NavDrawerEventMap>; // Adjust the type as necessary
  toastRef: any; // Adjust the type as necessary
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  visible,
  onClose,
  selectedTeam,
  afterSubmitCallback: _afterSubmitCallback,
  toastRef,
}) => {
  const { t } = useTranslation();
  const [isCancelingStripSubscription, setIsCancelingStripSubscription] =
    useState(false);

  const [cancelStripeSubscription] = useCancelStripeSubscriptionMutation();

  const handleCancelSubscription = async () => {
    if (!isCancelingStripSubscription && selectedTeam) {
      setIsCancelingStripSubscription(true);
      await cancelStripeSubscription({
        variables: { id: selectedTeam.id },
        refetchQueries: [
          ...(selectedTeam.uuid
            ? [
                {
                  query: GetTeamDetailDocument,
                  variables: { id: selectedTeam.uuid, includePending: true },
                },
              ]
            : []),
        ],
        onCompleted: () => {
          onClose();
          toastRef?.current?.show(
            <Box flexDirection='row' alignItems='center' gap='xs'>
              <Icon name='Check' width={24} height={24} color='white' />
              <Text variant='labelSmall' color='white'>
                {t(
                  'models:profile.subscriptionPlans.yourSubscriptionHasBeenTerminated'
                )}
              </Text>
            </Box>,
            { duration: 3000 }
          );
          setIsCancelingStripSubscription(false);
        },
        onError: () => {
          setIsCancelingStripSubscription(false);
        },
      });
    }
  };

  return (
    <WebModal
      accessibilityLabel='confirmation-modal'
      marginTop='m'
      visible={visible}
      width={483}
      height={184}
      title={t('models:profile.subscriptionPlans.weAreSadToSeeYouGo')}
      titleVariant='labelLarge'
      titleColor='textPrimary'
      marginHorizontal='l'
      onClose={onClose}>
      <Box flex={1} mx='l'>
        <Box alignItems='flex-start' mt='l'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {t('models:profile.subscriptionPlans.beforeYouLeave')}
          </Text>
        </Box>

        <Box
          mt='l'
          flexDirection='row'
          alignItems='flex-end'
          justifyContent='flex-end'>
          <TouchableOpacity
            onPress={() => {
              onClose();
              const url = 'mailto:info@tasktag.com';
              window.open(url, '_blank');
            }}>
            <Box
              borderRadius='xs'
              borderWidth={1}
              borderColor='black'
              width={117}
              height={48}
              mr='xs'
              justifyContent='center'
              alignContent='center'
              alignItems='center'
              accessibilityLabel={t(
                'models:profile.subscriptionPlans.getInTouch'
              )}>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:profile.subscriptionPlans.getInTouch')}
              </Text>
            </Box>
          </TouchableOpacity>

          <TouchableOpacity onPress={handleCancelSubscription}>
            <Box
              backgroundColor={
                isCancelingStripSubscription ? 'grey02' : 'black'
              }
              borderRadius='xs'
              width={125}
              height={48}
              justifyContent='center'
              alignContent='center'
              alignItems='center'
              accessibilityLabel={t(
                'models:profile.subscriptionPlans.cancelPlan'
              )}>
              <Text variant='labelSmall' color='white'>
                {t('models:profile.subscriptionPlans.cancelPlan')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      </Box>
    </WebModal>
  );
};

export default CancelSubscriptionModal;
